import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, FileText, Tag, Video } from 'lucide-react';

const ProjectCard = ({ project, showPatternBadge = false, onOpen }) => {
    // Show the "Cartamodello Gratuito" badge ONLY when a real pattern PDF has been uploaded
    const hasRealPatternPdf = project?.patternPdfUrls && project.patternPdfUrls.length > 0;

    // Determine rotation direction based on ID for deterministic randomness
    const idNum = typeof project.id === 'number'
        ? project.id
        : String(project.id || '')
            .split('')
            .reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
    const rotateDir = idNum % 2 === 0 ? 1 : -1;

    // SEO Helper: Gestisce il click sul link mantenendo la navigazione SPA
    const handleClick = (e) => {
        e.preventDefault(); // Evita il refresh della pagina
        if (onOpen) onOpen(project);
    };

    return (
        <motion.div
            layout
            variants={{
                hidden: { opacity: 0, scale: 0.8, y: 50 },
                visible: {
                    opacity: 1,
                    scale: 1,
                    y: 0,
                    rotate: 0,
                    clipPath: "inset(0% 0% 0% 0%)",
                    transition: { type: "spring", stiffness: 100, damping: 15 }
                },
                exit: {
                    opacity: 0,
                    scale: 0.8,
                    y: 100,
                    rotate: 15 * rotateDir,
                    clipPath: "inset(0% 0% 100% 0%)",
                    transition: { duration: 0.4, ease: "easeIn" }
                }
            }}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="group relative bg-white p-3 shadow-xl hover:shadow-2xl transition-shadow duration-300 stitched-border h-full flex flex-col"
        >
            <div className="relative overflow-hidden rounded-lg aspect-[4/5] bg-gray-100">
                <img
                    src={project.img}
                    alt={project.title} // Importante per SEO: Alt text dell'immagine
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                />

                {/* Badges Container */}
                <div className="absolute top-3 right-3 flex flex-col gap-2 items-end z-20">
                    {/* Pattern Indicator Badge */}
                    {showPatternBadge && hasRealPatternPdf && (
                        <div className="bg-white/95 backdrop-blur-sm text-green-700 px-3 py-1.5 rounded-full shadow-md text-xs font-bold flex items-center gap-1.5 stitched-border !border-green-500 !border-[1px] !shadow-none">
                            <FileText size={12} fill="currentColor" className="text-green-500" />
                            <span>Cartamodello Gratuito</span>
                        </div>
                    )}

                    {/* Video Tutorial Badge */}
                    {project.tutorialLink && (
                        <div className="bg-white/95 backdrop-blur-sm text-red-600 px-3 py-1.5 rounded-full shadow-md text-xs font-bold flex items-center gap-1.5 stitched-border !border-red-500 !border-[1px] !shadow-none">
                            <Video size={12} fill="currentColor" className="text-red-500" />
                            <span>Video Tutorial</span>
                        </div>
                    )}
                </div>

                {/* Overlay SEO Friendly: L'intero blocco ora è cliccabile come link ma gestito in JS */}
                <a
                    href={`/progetto/${project.id}`}
                    onClick={handleClick}
                    className="absolute inset-0 bg-[#4e342e]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center text-white p-4 text-center z-10 decoration-none"
                    aria-label={`Scopri il progetto ${project.title}`}
                >
                    <h3 translate="no" className="notranslate font-hand text-3xl mb-2 translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-75 text-white m-0">{project.title}</h3>
                    <p className="text-sm uppercase tracking-wider mb-4 translate-y-4 group-hover:translate-y-0 transition-transform duration-300 delay-100 text-gray-200 m-0">{project.category}</p>

                    {/* Pulsante Visivo (ora dentro al link <a>) */}
                    <div className="bg-white text-craft-red px-6 py-2 rounded-full font-bold text-sm flex items-center gap-2 transform translate-y-8 group-hover:translate-y-0 transition-transform duration-300 delay-150 hover:bg-craft-cream hover:scale-105 cursor-pointer">
                        Scopri <ArrowRight size={14} />
                    </div>
                </a>
            </div>

            <div className="mt-4 px-2 pb-2 flex-grow">
                <div className="flex justify-between items-start mb-2">
                    <div className="flex flex-col text-left">
                        <span className="text-xs font-bold text-craft-red uppercase tracking-wider mb-1 flex items-center gap-1">
                            <Tag size={10} /> {project.category}
                        </span>
                        {/* Link anche sul titolo per massima accessibilità SEO */}
                        <a translate="no" href={`/progetto/${project.id}`} onClick={handleClick} className="notranslate font-bold text-lg text-craft-dark font-hand decoration-none hover:text-craft-red transition-colors">
                            {project.title}
                        </a>
                        {project.createdAt && (
                            <span className="text-xs text-gray-500 mt-1">
                                Pubblicato:{" "}
                                {new Date(project.createdAt).toLocaleDateString("it-IT", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                })}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default ProjectCard;